/**
 * EditField Component
 *
 * Renders an editable field based on its type.
 * Supports all field types from Object Manager.
 */

import React from 'react';
import { getPicklistValuesAsStrings, type CustomField } from '../../services/queries/fieldQueries';
import { StandardLookupField } from '../standard';

interface EditFieldProps {
  field: CustomField;
  value: any;
  onChange: (value: any) => void;
  error?: string;
  disabled?: boolean;
}

export const EditField: React.FC<EditFieldProps> = ({
  field,
  value,
  onChange,
  error,
  disabled = false
}) => {
  const baseInputClasses = `
    w-full px-3 py-2
    border rounded-lg
    bg-white dark:bg-[#0f172a]
    text-gray-900 dark:text-white
    border-gray-300 dark:border-gray-600
    focus:border-blue-500 focus:ring-1 focus:ring-blue-500
    disabled:opacity-50 disabled:cursor-not-allowed
    ${error ? 'border-red-500 dark:border-red-500' : ''}
  `.trim().replace(/\s+/g, ' ');

  const renderInput = () => {
    // Read-only fields (system fields)
    if (field.fieldName === 'createdDate' ||
        field.fieldName === 'createdBy' ||
        field.fieldName === 'lastModified' ||
        field.fieldName === 'lastModifiedBy' ||
        field.fieldName === 'id') {
      return (
        <input
          type="text"
          value={value || ''}
          disabled
          className={baseInputClasses}
        />
      );
    }

    switch (field.fieldType) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
        // Get maxLength: use field.length if set, otherwise use default (255 for text fields)
        const textMaxLength = field.length || 255;
        return (
          <input
            type={
              field.fieldType === 'email' ? 'email' :
              field.fieldType === 'url' ? 'url' :
              field.fieldType === 'phone' ? 'tel' :
              'text'
            }
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            placeholder={field.description || `Enter ${field.fieldLabel.toLowerCase()}`}
            className={baseInputClasses}
            required={field.required}
            maxLength={textMaxLength}
          />
        );

      case 'textarea':
      case 'longtextarea':
        // Get maxLength: use field.length if set, otherwise use defaults (1000 for textarea, 32000 for longtextarea)
        const textareaMaxLength = field.length || (field.fieldType === 'longtextarea' ? 32000 : 1000);
        return (
          <textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            rows={4}
            placeholder={field.description || `Enter ${field.fieldLabel.toLowerCase()}`}
            className={baseInputClasses}
            required={field.required}
            maxLength={textareaMaxLength}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : null)}
            disabled={disabled}
            placeholder={field.description}
            className={baseInputClasses}
            required={field.required}
          />
        );

      case 'currency':
        return (
          <div className="relative">
            <span className="absolute left-3 top-2.5 text-gray-500 dark:text-gray-400">$</span>
            <input
              type="number"
              step="0.01"
              value={value ?? ''}
              onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : null)}
              disabled={disabled}
              placeholder="0.00"
              className={`${baseInputClasses} pl-7`}
              required={field.required}
            />
          </div>
        );

      case 'percent':
        return (
          <div className="relative">
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={value ?? ''}
              onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : null)}
              disabled={disabled}
              placeholder="0"
              className={`${baseInputClasses} pr-7`}
              required={field.required}
            />
            <span className="absolute right-3 top-2.5 text-gray-500 dark:text-gray-400">%</span>
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            value={value ? new Date(value).toISOString().split('T')[0] : ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={baseInputClasses}
            required={field.required}
          />
        );

      case 'datetime':
        return (
          <input
            type="datetime-local"
            value={value ? new Date(value).toISOString().slice(0, 16) : ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={baseInputClasses}
            required={field.required}
          />
        );

      case 'time':
        return (
          <input
            type="time"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={baseInputClasses}
            required={field.required}
          />
        );

      case 'checkbox':
        return (
          <div className="flex items-center gap-2 h-10">
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {field.description || field.fieldLabel}
            </span>
          </div>
        );

      case 'picklist':
        return (
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={baseInputClasses}
            required={field.required}
          >
            <option value="">Select {field.fieldLabel}</option>
            {getPicklistValuesAsStrings(field.picklistValues).map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'multipicklist':
        return (
          <select
            multiple
            value={Array.isArray(value) ? value : (value ? [value] : [])}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, opt => opt.value);
              onChange(selected);
            }}
            disabled={disabled}
            className={`${baseInputClasses} min-h-[100px]`}
            required={field.required}
          >
            {getPicklistValuesAsStrings(field.picklistValues).map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'lookup':
      case 'masterdetail':
        return (
          <StandardLookupField
            field={field}
            value={String(value || '')}
            onChange={onChange}
            error={error}
            required={field.required}
            disabled={disabled}
          />
        );

      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            placeholder={field.description}
            className={baseInputClasses}
            required={field.required}
          />
        );
    }
  };

  const isLookupOrMasterDetail = field.fieldType === 'lookup' || field.fieldType === 'masterdetail';

  return (
    <div className={field.fieldType === 'checkbox' ? '' : 'space-y-1'}>
      {field.fieldType !== 'checkbox' && !isLookupOrMasterDetail && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {field.fieldLabel}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {renderInput()}

      {error && !isLookupOrMasterDetail && (
        <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
          <span>⚠️</span>
          {error}
        </p>
      )}

      {field.description && field.fieldType !== 'checkbox' && !isLookupOrMasterDetail && !error && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {field.description}
        </p>
      )}
    </div>
  );
};
